import React from 'react';
import { Button as MuiButton } from '@material-ui/core';
import { fade } from '@material-ui/core/styles/colorManipulator';
import { withStyles } from '@material-ui/styles';
import LoadingIcon from 'mdi-react/LoadingIcon';
import PropTypes from 'prop-types';


const styles = theme => ({
  root: {
    boxShadow: 'None',
    borderRadius: '0.375rem',
    height: '2.5rem',
    padding: '0 !important',
    minWidth: '6rem !important',
    textTransform: 'None',
    '&:hover': {
      boxShadow: 'None',
      // reset on touch devices
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
    '&:active': {
      boxShadow: 'None',
    },
  },
  sizeSmall: {
    height: '2rem !important',
  },
  sizeLarge: {
    height: '3rem !important',
  },
  label: {
    textTransform: 'None',
    maxWidth: '16rem',
    display: 'flex',
    padding: '0 0.75rem !important',
  },
  disabled: {
    backgroundColor: theme.palette.gray.main,
    color: theme.palette.gray.contrastText,
  },
  contained: {
    '&$disabled': {
      backgroundColor: theme.palette.gray.main,
      color: theme.palette.gray.contrastText,
      boxShadow: 'none',
    },
  },
  outlined: {
    '&$disabled': {
      backgroundColor: `${theme.palette.common.white} !important`,
      borderColor: `${theme.palette.gray.light} !important`,
      color: `${theme.palette.gray.light} !important`,
    },
  },
  text: {
    '&$disabled': {
      backgroundColor: 'transparent',
      border: 'None',
      color: theme.palette.gray.light,
    },
  },
  textPrimary: {
    fontWeight: 600,
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.transparent,
    '&:hover': {
      backgroundColor: theme.palette.transparent,
      color: theme.palette.primary.dark,
    },
    '&:active': {
      backgroundColor: theme.palette.transparent,
      color: theme.palette.secondary.darkest,
    },
  },
  textPrimaryContained: {
    color: theme.palette.primary.main,
    backgroundColor: fade(theme.palette.primary.main, 0.1),
    '&:hover': {
      backgroundColor: fade(theme.palette.primary.main, 0.2),
      color: theme.palette.primary.main,
    },
    '&:active': {
      backgroundColor: fade(theme.palette.primary.main, 0.2),
      color: theme.palette.primary.main,
    },
    '&$disabled': {
      backgroundColor: theme.palette.gray.main,
      color: theme.palette.gray.contrastText,
      boxShadow: 'none',
    },
  },
  textSecondary: {
    color: theme.palette.gray.dark,
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: theme.palette.secondary.dark,
      color: theme.palette.secondary.main,
    },
    '&:active': {
      backgroundColor: theme.palette.secondary.darkest,
      color: theme.palette.secondary.main,
    },
  },
  containedPrimary: {
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    '&:active': {
      backgroundColor: theme.palette.primary.darkest,
    },
  },
  containedPrimaryCTA: {
    color: theme.palette.primaryCTA.contrastText,
    backgroundColor: theme.palette.primaryCTA.main,
    '&:hover': {
      backgroundColor: theme.palette.primaryCTA.dark,
    },
    '&:active': {
      backgroundColor: theme.palette.primaryCTA.darkest,
    },
  },
  containedAccent3: {
    color: theme.palette.accent3.contrastText,
    backgroundColor: theme.palette.accent3.main,
    '&:hover': {
      backgroundColor: theme.palette.accent3.dark,
    },
    '&:active': {
      backgroundColor: theme.palette.accent3.darkest,
    },
  },
  outlinedWhite: {
    border: '0.063rem solid',
    backgroundColor: theme.palette.white,
    borderColor: theme.palette.secondary['300'],
    color: theme.palette.secondary.text,
    '&:hover': {
      backgroundColor: theme.palette.white,
      borderColor: theme.palette.secondary.main,
    },
    '&:active': {
      borderColor: theme.palette.secondary.main,
      backgroundColor: theme.palette.white,
    },
  },
  outlinedSecondary: {
    border: '0.063rem solid !important',
    borderColor: `${theme.palette.secondary.main} !important`,
    color: `${theme.palette.secondary.main} !important`,
    '&:hover': {
      border: 'None !important',
      borderColor: `${theme.palette.secondary.main} !important`,
      backgroundColor: `${theme.palette.secondary.dark} !important`,
    },
    '&:active': {
      border: 'None !important',
      borderColor: `${theme.palette.secondary.main} !important`,
      backgroundColor: `${theme.palette.secondary.darkest} !important`,
    },
  },
  outlinedSecondaryDashed: {
    border: '0.063rem dashed !important',
    borderColor: `${theme.palette.secondary.main} !important`,
    color: `${theme.palette.secondary.main} !important`,
    '&:hover': {
      border: 'None !important',
      borderColor: `${theme.palette.secondary.main} !important`,
      backgroundColor: `${theme.palette.secondary.dark} !important`,
    },
    '&:active': {
      border: 'None !important',
      borderColor: `${theme.palette.secondary.main} !important`,
      backgroundColor: `${theme.palette.secondary.darkest} !important`,
    },
    '&$disabled': {
      backgroundColor: theme.palette.gray.main,
      color: `${theme.palette.gray.contrastText} !important`,
      borderColor: `${theme.palette.gray.light} !important`,
    },
  },
  outlinedDelete: {
    border: '0.063rem solid',
    borderColor: theme.palette.gray.light,
    color: theme.palette.gray.dark,
    '&:hover': {
      border: 'None',
      color: theme.palette.delete.contrastText,
      backgroundColor: theme.palette.delete.dark,
    },
    '&:active': {
      border: 'None',
      color: theme.palette.delete.contrastText,
      backgroundColor: theme.palette.delete.darkest,
    },
  },
  btnSpan: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
});

const Button = (props) => {
  const { classes } = props;
  return (
    <MuiButton
      classes={{
        root: classes.root,
        text: classes.text,
        contained: classes.contained,
        outlined: classes.outlined,
        disabled: classes.disabled,
        containedPrimary: classes.containedPrimary,
        containedPrimaryCTA: classes.containedPrimaryCTA,
        containedAccent3: classes.containedAccent3,
        outlinedSecondary: classes.outlinedSecondary,
        outlinedDelete: classes.outlinedDelete,
        outlinedWhite: classes.outlinedWhite,
        textPrimary: classes.textPrimary,
        textSecondary: classes.textSecondary,
        textPrimaryContained: classes.textPrimaryContained,
        label: classes.label,
        sizeSmall: classes.sizeSmall,
        sizeLarge: classes.sizeLarge,
      }}
      {...props}
      disableFocusRipple
      disableRipple
      disabled={props.disabled || props.loading}
    >
      <span className={classes.btnSpan}>
        {!props.loading ?
            props.children : <div className="loader"><LoadingIcon /></div>
        }
      </span>
    </MuiButton>
  );
};

Button.propTypes = {
  color: PropTypes.string,
  size: PropTypes.string,
  disabled: PropTypes.bool,
};

Button.defaultProps = {
  color: 'primary',
  size: '',
  disabled: false,
};

export default withStyles(styles)(Button);
